import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, finalize, map, of, tap } from 'rxjs';

import { AccessToken, CommunicationToken, SessionToken, User } from '@shared/models';
import { LoginData, RegistrationData, ResetConfirmData } from '@shared/types';

import { UrlTokenInfo } from '@/external-access/models';

import { environment } from '@env';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    public activeUser = new BehaviorSubject<User | undefined>(undefined);
    public adminMode = new BehaviorSubject<boolean>(false);

    public tokenInfo = new BehaviorSubject<UrlTokenInfo | undefined>(undefined);
    public endUserToken?: CommunicationToken;
    public externalSsn = ''; //Social security number for the external user

    constructor(private http: HttpClient) {}

    popLoginRedirect(): string {
        const redirect = localStorage.getItem(environment.storageKeys.REDIRECT_URL) ?? environment.defaultLoginPath;
        localStorage.removeItem(environment.storageKeys.REDIRECT_URL);
        return redirect;
    }

    setLoginRedirect(value: string) {
        localStorage.setItem(environment.storageKeys.REDIRECT_URL, value);
    }

    logout(): Observable<any> {
        return this.http
            .post(environment.endpoints.logout, {
                refresh: this.getRefreshToken(),
            })
            .pipe(
                finalize(() => {
                    this.clearSessionTokens();
                    this.clearLastOrganisation();
                    this.activeUser.next(undefined);
                    this.adminMode.next(false);
                    this.endUserToken = undefined;
                }),
            );
    }

    private getLastOrganisationKey() {
        return `${environment.storageKeys.ORGANISATION_HISTORY_PREFIX}.${this.activeUser.value?.id ?? 0}`;
    }

    setLastOrganisation(organisationId: number) {
        localStorage.setItem(this.getLastOrganisationKey(), String(organisationId));
    }

    private clearLastOrganisation() {
        localStorage.removeItem(this.getLastOrganisationKey());
    }

    getLastOrganisation(): { organisationId?: number; admin: boolean } {
        const user = this.activeUser.value;
        let organisationId = undefined;
        let admin = false;
        if (user != undefined) {
            const hist = localStorage.getItem(this.getLastOrganisationKey());
            if (hist !== null && !isNaN(Number(hist))) {
                organisationId = Number(hist);
                admin = user.isStaff && !user.roles.map((role) => role.organisationId).includes(organisationId);
            }
        }
        return { organisationId, admin };
    }

    register(registrationData: RegistrationData): Observable<void> {
        return this.http.post<void>(environment.endpoints.register, {
            email: registrationData.email,
            first_name: registrationData.firstName,
            last_name: registrationData.lastName,
            password1: registrationData.password1,
            password2: registrationData.password2,
        });
    }

    login(data: LoginData): Observable<SessionToken> {
        return this.http.post<SessionToken>(environment.endpoints.login, data).pipe(
            map<any, SessionToken>((tokenData: any) => new SessionToken().deserialize(tokenData)),
            tap<SessionToken>(this.saveSessionToken),
            tap<SessionToken>((token: SessionToken) => this.activeUser.next(token.user)),
            tap(() => {
                this.adminMode.next(false);
                this.endUserToken = undefined;
            }),
        );
    }

    loginWithCode(code: string): Observable<SessionToken> {
        return this.http.post<SessionToken>(environment.endpoints.loginWithCode, { code }).pipe(
            map<any, SessionToken>((value: any) => new SessionToken().deserialize(value)),
            tap<SessionToken>((token) => this.saveSessionToken(token)),
            tap<SessionToken>((token: SessionToken) => this.activeUser.next(token.user)),
            tap(() => {
                this.adminMode.next(false);
                this.endUserToken = undefined;
            }),
        );
    }

    getTokenUser(): Observable<User> {
        return this.http.get<User>(environment.endpoints.currentUser).pipe(
            tap<User>((user) => this.activeUser.next(new User().deserialize(user))),
            tap(() => {
                this.adminMode.next(false);
                this.endUserToken = undefined;
            }),
        );
    }

    updateTokenUserLocale(locale: string): Observable<User> {
        return this.http
            .patch<User>(environment.endpoints.currentUser, { locale })
            .pipe(tap<User>((user) => this.activeUser.next(new User().deserialize(user))));
    }

    /**
     * This will try to restore the active session if one exists.
     * @returns Observable<boolean>  whether or not a valid session exists.
     **/
    restoreSession(): Observable<boolean> {
        let token = this.getAccessToken();
        if (!token || this.isAccessTokenExpired()) {
            return of(false);
        } else if (token) {
            return this.getTokenUser().pipe(
                map<User, boolean>((user) => {
                    return user !== undefined;
                }),
            );
        }
        return of(false);
    }

    refreshAccessToken(): Observable<boolean> {
        if (this.isRefreshTokenExpired() || !this.hasRefreshToken()) {
            return of(false);
        }

        return this.http
            .post(environment.endpoints.refreshToken, {
                refresh: this.getRefreshToken(),
            })
            .pipe(
                map<any, AccessToken>((data) => new AccessToken().deserialize(data)),
                tap<AccessToken>((token) => this.saveSessionToken(token)),
                map<AccessToken, boolean>((token) => {
                    return token.accessToken !== undefined && token.accessTokenExpiration !== undefined;
                }),
            );
    }

    clearSessionTokens() {
        localStorage.removeItem(environment.storageKeys.JWT_ACCESS_TOKEN);
        localStorage.removeItem(environment.storageKeys.JWT_ACCESS_EXPIRY);
        localStorage.removeItem(environment.storageKeys.JWT_REFRESH_TOKEN);
        localStorage.removeItem(environment.storageKeys.JWT_REFRESH_EXPIRY);
    }

    saveSessionToken(session: SessionToken | AccessToken) {
        if (session.accessToken && session.accessTokenExpiration) {
            localStorage.setItem(environment.storageKeys.JWT_ACCESS_TOKEN, session.accessToken);
            localStorage.setItem(environment.storageKeys.JWT_ACCESS_EXPIRY, session.accessTokenExpiration.toISOString());
        }
        if (session instanceof SessionToken && session.refreshToken && session.refreshTokenExpiration) {
            localStorage.setItem(environment.storageKeys.JWT_REFRESH_TOKEN, session.refreshToken);
            localStorage.setItem(environment.storageKeys.JWT_REFRESH_EXPIRY, session.refreshTokenExpiration.toISOString());
        }
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(environment.storageKeys.JWT_REFRESH_TOKEN);
    }

    getAccessToken(): string | null {
        return localStorage.getItem(environment.storageKeys.JWT_ACCESS_TOKEN);
    }

    hasAccessToken(): boolean {
        return localStorage.getItem(environment.storageKeys.JWT_ACCESS_TOKEN) !== null;
    }

    hasRefreshToken(): boolean {
        return localStorage.getItem(environment.storageKeys.JWT_REFRESH_TOKEN) !== null;
    }

    isAccessTokenExpired(): boolean {
        const expiry = localStorage.getItem(environment.storageKeys.JWT_ACCESS_EXPIRY);
        return expiry === null || new Date(expiry) < new Date();
    }

    isRefreshTokenExpired(): boolean {
        const expiry = localStorage.getItem(environment.storageKeys.JWT_REFRESH_EXPIRY);
        return expiry === null || new Date(expiry) < new Date();
    }

    requestResetPasswordEmail(email: string): Observable<any> {
        return this.http.post(environment.endpoints.passwordReset, { email: email });
    }

    resetPassword(data: ResetConfirmData): Observable<any> {
        return this.http.post(environment.endpoints.passwordResetConfirm, {
            uid: data.uid,
            token: data.token,
            email: data.email,
            new_password1: data.newPassword1,
            new_password2: data.newPassword2,
        });
    }

    resendVerificationEmail(email: string): Observable<any> {
        return this.http.post(environment.endpoints.resendEmail, {
            email: email,
        });
    }

    verifyEmail(code: string): Observable<any> {
        return this.http.post(environment.endpoints.verifyEmail, {
            key: code,
        });
    }

    getCommunicationToken(token: string, ssn: string): Observable<CommunicationToken> {
        return this.http
            .post<any>(environment.endpoints.communicationToken, {
                tax_token: token,
                social_security_number: ssn,
            })
            .pipe(map<any, CommunicationToken>((token) => new CommunicationToken().deserialize(token)));
    }

    getUrlTokenInfo(token: string): Observable<UrlTokenInfo> {
        return this.http
            .post<any>(environment.endpoints.urlTokenInfo, {
                'tax_token': token,
            })
            .pipe(
                map<any, UrlTokenInfo>((tokenInfo) => new UrlTokenInfo().deserialize(tokenInfo)),
                tap<UrlTokenInfo>((tokenInfo) => this.tokenInfo.next(tokenInfo)),
            );
    }
}
